import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from "@angular/core";
import { MessageService } from "primeng/api";
import * as _ from "lodash";
import { TitleCasePipe } from "@angular/common";
import { Subject, takeUntil } from "rxjs";
import { SalesOrderService } from "src/app/inventory/sales-order/sales-order.service";
import { AppMainComponent } from "src/app/app.main.component";
import { Severity } from "src/app/inventory/constants/constants";
import { AppMainService } from "src/app/app-main.service";
import { ImportserialService } from "../../services/importserial.service";
import { MulticurrencyPipe } from "src/app/modules/shared/pipes/multicurrency.pipe";

@Component({
    selector: "app-apply-credit-memo",
    templateUrl: "./apply-credit-memo.component.html",
    styleUrls: ["./apply-credit-memo.component.scss"],
})
export class ApplyCreditMemoComponent implements OnInit, OnChanges, OnDestroy {
    @Input() accountId: number;
    @Input() invoiceId: number = 0;
    @Input() salesOrderId: number = 0;
    @Input() receivedPaymentId: number = 0;
    @Input() displayCreditSelect: boolean = false;
    @Input() reloadData: boolean = false;
    @Output() appliedCreditMemoEventEmitter = new EventEmitter<{selectedCredits: any[], appliedCreditMemoTotal: number, totalCreditAmount: number, type: string, receivedPayment: any | null}>();
    @Output() onCloseCreditMemoSelectionModal = new EventEmitter();

    protected ngUnsubscribe$: Subject<void> = new Subject<void>();

    availableCredits: any[] = [];
    selectedCredits: any[] = [];
    creditAppliedTotal: number = 0;
    receivedPayment: any = {};
    dialogType: string = '';
    actionPerformed: boolean = false;
    // fraction digits
    fractionDigits: number = 0;
    // currency
    baseCurrency: any;
    orgCurrency = [];

    constructor(
        public app: AppMainComponent,
        private salesOrderService: SalesOrderService,
        private messageService: MessageService,
        private titlecasePipe: TitleCasePipe,
        private appMainService: AppMainService,
        private importService: ImportserialService,
        private multiCurrency: MulticurrencyPipe
    ) { }

    ngOnInit(): void {
        this.getFractionDigits();
    }
    
    ngOnChanges(changes: SimpleChanges): void {
        let shouldLoadData = false;
        this.actionPerformed = false;
        if (changes.accountId && changes.accountId.currentValue) {
            this.accountId = changes.accountId.currentValue;
            shouldLoadData = true;
        }
        if (changes.receivedPaymentId && changes.receivedPaymentId.currentValue) {
            this.receivedPaymentId = changes.receivedPaymentId.currentValue;
            this.dialogType = 'view';   
            this.getReceivePaymentById(this.receivedPaymentId);
        }
        if (changes.reloadData && changes.reloadData.currentValue) {
            this.reloadData = changes.reloadData.currentValue;
            shouldLoadData = true;
        }
        if(shouldLoadData){
            this.getCreditMemo();
        }
    }
    ngOnDestroy(): void {
        this.actionPerformed = false;
        if (this.ngUnsubscribe$) {
            this.ngUnsubscribe$.next();
            this.ngUnsubscribe$.complete();
            this.ngUnsubscribe$.unsubscribe();
        }
    }
    getFractionDigits() {
        this.importService
            .getDecimalValue()
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe({
                next: (value) => {
                    if(value){
                        this.fractionDigits = value;
                    }
                },
                error: (error) => {},
            });
    }

    getCreditMemo() {
        this.resetCreditMemoData();
        this.importService
            .getOrgCurrency()
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe((value) => {
                if (value) {
                    this.baseCurrency = value.find((cr) => cr.isBaseCurrency);
                    this.orgCurrency = value;
                    this.fetchCreditMemos();
                }
        });
    }
    resetCreditMemoData() {
        this.availableCredits = [];
        this.selectedCredits = [];
    }
    fetchCreditMemos() {
        this.appMainService.isShowSpinner("loadCreditMemos");
        this.salesOrderService.getCreditMemoByAccount(this.accountId, null, this.invoiceId ?? 0, this.salesOrderId ?? 0)
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe({
                next: res => {
                    this.handleCreditMemosResponse(res);
                    this.appMainService.isHideSpinner("loadCreditMemos");
                },
                error: () => {
                    this.appMainService.isHideSpinner("loadCreditMemos");
                    this.showErrorMessage()
                }
            });
    }
    handleCreditMemosResponse(res) {
        const data = this.transformCreditMemoData(res);
        this.availableCredits = data;
        this.selectedCredits = this.getSelectedCredits(data);
        const totalCreditAmount = this.calculateTotalCreditAmount(data);
        const appliedCreditMemoTotal = this.calculateAppliedCreditMemoTotal(this.selectedCredits);
        this.creditAppliedTotal = appliedCreditMemoTotal;
        const path = this.buildTransactionPath();
        if (path != '') {
            this.fetchReceivedPayment(path, totalCreditAmount, appliedCreditMemoTotal);
        } else {
            this.emitCreditMemoEvent(totalCreditAmount, appliedCreditMemoTotal);
        }
    }
    transformCreditMemoData(data) {
        return data?.map(element => ({
            ...element,
            totalCreditAmount: Number(element.totalCreditAmount ?? 0) *  (element.exchangeRate ? element.exchangeRate : this.baseCurrency?.exchangeRate),
            appliedAmount: Number(element.appliedAmount ?? 0) * (element.exchangeRate ? element.exchangeRate : this.baseCurrency?.exchangeRate),
            pendingCreditAmount: Number(element.pendingCreditAmount ?? 0) * (element.exchangeRate ? element.exchangeRate : this.baseCurrency?.exchangeRate),
            creditMemoDate: this.app._showDateTimeWithZone(element.creditMemoDate),
            type: 'creditMemo',
        })) || [];
    }
    getSelectedCredits(data) {
        return data.filter(c => c.isSelected);
    }
    calculateTotalCreditAmount(data) {
        return data.reduce((total, d) => total + Number(d.pendingCreditAmount), 0);
    }
    calculateAppliedCreditMemoTotal(selectedCredits) {
        return selectedCredits.reduce((total, d) => total + Number(d.pendingCreditAmount), 0);
    }
    buildTransactionPath() {
        if (this.invoiceId > 0) {
            return `invoiceId=${this.invoiceId}`;
        } else if (this.salesOrderId > 0) {
            return `salesOrderId=${this.salesOrderId}`;
        }
        return '';
    }
     
    fetchReceivedPayment(path, totalCreditAmount, appliedCreditMemoTotal) {
        this.appMainService.isShowSpinner("loadreceivedPayment");
        this.salesOrderService.getReceivePaymentByTransaction(path)
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe({
                next: res => {
                    this.handleReceivedPaymentResponse(res, totalCreditAmount, appliedCreditMemoTotal);
                    this.appMainService.isHideSpinner("loadreceivedPayment");
                },
                error: () => {
                    this.appMainService.isHideSpinner("loadreceivedPayment");
                    this.showErrorMessage();
                }
            });
    }
     
    handleReceivedPaymentResponse(receivedPayment, totalCreditAmount, appliedCreditMemoTotal) {
        this.receivedPayment = receivedPayment;
        this.emitCreditMemoEvent(totalCreditAmount, appliedCreditMemoTotal);
    }
     
    emitCreditMemoEvent(totalCreditAmount, appliedCreditMemoTotal) {
        this.appliedCreditMemoEventEmitter.emit({
            selectedCredits: this.selectedCredits ?? [],
            appliedCreditMemoTotal: appliedCreditMemoTotal,
            totalCreditAmount: totalCreditAmount,
            type: '',
            receivedPayment: this.receivedPayment
        });
    }

    getReceivePaymentByTransaction(data: string) {
        this.salesOrderService.getReceivePaymentByTransaction(data)
            .pipe(takeUntil(this.ngUnsubscribe$))
			.subscribe({
                next: res => {
                    this.receivedPayment = res;
                },
                error: () => {
                    this.showErrorMessage();
                }
			});
    }
    getReceivePaymentById(id){
        this.salesOrderService.getReceivedPaymentsById(id)  
            .pipe(takeUntil(this.ngUnsubscribe$))     
            .subscribe({         
                next: res => {
                    const data = res?.receivedPaymentCreditMemo?.map(element => ({
                        ...element,
                        number: element?.creditMemoNumber ?? "",
                        creditMemoStatus: element?.status ?? "",
                        totalCreditAmount: element?.creditAmount ?? 0,
                        appliedAmount: element?.appliedCreditAmount ?? 0,
                        creditMemoDate: this.app._showDateTimeWithZone(element.creditMemoDate),
                    }));
                    this.availableCredits = data;
                    this.selectedCredits = [];
                },       
                error: (error: any) => { 
                    this.showErrorMessage();       
                }     
            });
    }
    calTotalCredit(c) {
        this.actionPerformed = true;
        this.creditAppliedTotal = this.selectedCredits?.reduce((total, d) => total + Number(d.pendingCreditAmount), 0);
    }
    closeCreditSelectionModal(){
        const credits = this.availableCredits?.filter(c => c.isSelected == true);
        if(credits?.length == 0){
            this.selectedCredits = [];
        } else {
            this.selectedCredits = credits;
        }
        this.onCloseCreditMemoSelectionModal.emit({});
    }
    applySelectedCredit(){
        this.displayCreditSelect = false;
        let totalCreditAmount = this.availableCredits?.reduce((total, d) => total + Number(d.pendingCreditAmount), 0);
        this.selectedCredits = this.reCalculateCreditAmount();
        this.appliedCreditMemoEventEmitter.emit({selectedCredits: this.selectedCredits ?? [], appliedCreditMemoTotal: this.creditAppliedTotal, totalCreditAmount: totalCreditAmount, type: 'apply', receivedPayment: this.receivedPayment});
    }
    reCalculateCreditAmount(){
        const processedCredits = this.selectedCredits.map(credit => {
            if (credit.type === 'creditMemo') {
                return {
                    ...credit,
                    totalCreditAmount: this.divideAmount(credit.totalCreditAmount,  credit.exchangeRate ? credit.exchangeRate : this.baseCurrency?.exchangeRate),
                    appliedAmount: this.divideAmount(credit.appliedAmount,  credit.exchangeRate ? credit.exchangeRate : this.baseCurrency?.exchangeRate),
                    pendingCreditAmount: this.divideAmount(credit.pendingCreditAmount,  credit.exchangeRate ? credit.exchangeRate : this.baseCurrency?.exchangeRate),
                };
            }
            return credit;
        });
        return processedCredits;
    }
    divideAmount(amount, exchangeRate: number = 1) {
        return exchangeRate !== 0 ? Number(amount ?? 0) / exchangeRate : 0;
    }
    showErrorMessage(){
        this.messageService.add({
            severity: Severity.Error,
            summary: this.titlecasePipe.transform(Severity.Error),
            detail: "Error occurred ...",
        });
    }
}
